import { Component, OnInit, ViewChild } from '@angular/core';
import { IAppResult } from '@app/core/model/appresults/appresult';
import { IAppResultFile } from '@app/core/model/appresults/appresult-file';
import { IBioSample } from '@app/core/model/bioSamples/bioSample';
import IResource from '@app/core/model/resource';
import { ResourceType } from '@app/core/model/resource-type';
import { IRun } from '@app/core/model/runs/run';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResultItem } from '@app/core/model/search/search-result-item';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { SearchQueryService } from '@app/core/services/search/resource-search/search-query.service';
import { IFileExtensionFilter } from '@app/search/datasource/search.datasource';
import { ModalComponentType } from '@app/shared/modals/base-modal/base-modal.component';
import { RadChooserModalComponent } from '@app/shared/modals/chooser/rad-chooser-modal.component';
import { IChooserModalInput, IChooserModalOutput, IInitialColumnFilter } from '@app/shared/modals/model/action-modal';
import { ChooserModalService } from '@app/shared/modals/services/chooser-modal.service';
import { ModalService } from '@app/shared/modals/services/modal.service';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { BsAppLaunchFormService } from '../../services/bs-app-launch-form.service';
import { ResourceChooserFormControl, ResourceReference } from './resource-chooser.control';
import { EditAllFieldsModalComponent } from '@app/shared/modals/edit-all-fields-modal/edit-all-fields-modal.component';
import { IEditAllFieldsModalInput, IEditAllFieldsModalOutput } from '@app/shared/modals/edit-all-fields-modal/edit-all-fields-modal.interface';
import { FormArray } from '@angular/forms';
import { BasespaceFormControl } from '../bs-app-launch-form-control';


@Component({
  selector: 'app-bs-app-launch-resource-chooser-control',
  templateUrl: './bs-app-launch-resource-chooser-control.component.html',
  styleUrls: ['./bs-app-launch-resource-chooser-control.component.scss']
})
export class BsAppLaunchResourceChooserControlComponent implements OnInit {
  @ViewChild('resourceChooserModal', { static: false }) public resourceChooserModal: any;

  formControl: ResourceChooserFormControl;

  public resourceType: SearchResourceType;
  public resourceDictionary: ISearchResourceConfig;
  public SearchResourceType = SearchResourceType;

  selectedItems$: BehaviorSubject<ISearchResultItem[]> = new BehaviorSubject(null);
  
  constructor(
    private searchResourceDictionaryService: SearchResourceDictionaryService,
    private modalService: ModalService,
    private bsAppLaunchFormService: BsAppLaunchFormService,
    private chooserModalService: ChooserModalService,
    private searchQueryService: SearchQueryService
  ) {}

  private subs = new SubSink();

  get resourceButtonTitle(): string {
    if(this.resourceDictionary) {
      if(this.formControl.multiselect) {
        return this.resourceDictionary.displayNamePlural.toLowerCase();
      } else {
        return this.resourceDictionary.displayNameSingular.toLowerCase();
      }
    } else {
      return '';
    }
  }

  getSelectedItemName(selectedItem: ISearchResultItem): string {
    return this.searchResourceDictionaryService.getSearchResourceName(
      selectedItem, 
      this.resourceType
    );
  }

  ngOnInit(): void {
    this.resourceType = this.formControl.resourceType;
    this.resourceDictionary = this.searchResourceDictionaryService.getResourceConfigByType(this.resourceType);
    if(this.formControl.resources) {
      const initialSearchItemResults = this.formControl.resources.map((resource) => this.generateSearchResultItem(resource))
      this.selectedItems$.next(initialSearchItemResults);
    }

    this.subs.sink = this.selectedItems$
    .pipe(filter((selectedItems) => !!selectedItems))
    .subscribe((selectedSearchResultItems) => {
      this.formControl.resources = selectedSearchResultItems.map((searchResultItem) => {
        const resource = this.getResourceFromSearchResultItem(searchResultItem);
        const resourceRef: ResourceReference = {
          id: resource.Id,
          name: this.getSelectedItemName(searchResultItem),
          type: null
        };
        return resourceRef;
      });

      // Update form control value with resource IDs (for biosamples, these are composite IDs)
      const updatedValue = selectedSearchResultItems.map((searchResultItem) => 
        this.getResourceFromSearchResultItem(searchResultItem).Id
      );

      this.formControl.setValue(updatedValue);
      this.bsAppLaunchFormService.formUpdateTrigger$.next({ controlId: this.formControl.id });
    });

    // Only used if displayFields property provided
    this.subs.sink = this.formControl.rowActionSubject.subscribe(({ action, rowIndex }) => {
      const searchItem = this.selectedItems$.value[rowIndex];
      this.removeSearchResult(searchItem);
    })
  }

  getResourceUrl(item: ISearchResultItem): string {
    const resource = this.getResourceFromSearchResultItem(item);
    
    switch (this.resourceType) {
      case SearchResourceType.Project:
        return BsApiEndPoints.getProjectDetailsRoute(resource.Id);
      case SearchResourceType.Run:
        return BsApiEndPoints.getRunDetailsRoute(resource.Id);
      case SearchResourceType.AppResult:
      case SearchResourceType.Dataset:
        const appResult = resource as IAppResult;
        return appResult.AppSession ? 
          BsApiEndPoints.getAppResultRoute(appResult.AppSession.Id, appResult.Id) :
          null;
      case SearchResourceType.File:
        const datasetFile = resource as any;
        return BsApiEndPoints.getDatasetFileRoute(datasetFile);
      case SearchResourceType.AppResultFile:
        const appResultFile = resource as IAppResultFile;
        return BsApiEndPoints.getAppResultFileRoute(appResultFile);
      case SearchResourceType.Sample:
        return BsApiEndPoints.getSampleDetailsRoute(resource.Id, resource.Name);
      case SearchResourceType.Biosample:
        return BsApiEndPoints.getBiosampleDetailsRoute(resource.Id);
      default:
        return '#';
    }
  }

  get tableId(): string {
    return `${this.resourceType}AppLaunchResourceChooser`;
  }

  openResourceChooser(newlyCreatedResourceId?: string): void {

    const extensionFilter: IFileExtensionFilter = this.formControl.extensionFilters ? {
      value: this.formControl.extensionFilters,
      strict: true
    } : null;

    const extendedBaseQuery = (this.formControl.baseExtendedQuery || this.formControl.searchQuery) ? this.searchQueryService.buildQueryString(
      this.formControl.baseExtendedQuery,
      this.formControl.searchQuery,
      [],
      null
    ) : null;

    let titleInfoPopoverContent;
    switch(this.resourceType) {
      case SearchResourceType.Biosample:
        titleInfoPopoverContent = `
          <ul class="fa-ul"> 
            <li> 
            <p>Only Biosamples containing FASTQ datasets with no failures can be used in this app. Biosamples that meet this criteria are displayed below; any others have been filtered out.</p> 
            </li> 
            <li> 
            <p>Some Biosamples may have been used with multiple library kits.</p> 
            </li> 
            <li> 
            <p>Some applications require FASTQ files that adhere to the <a href="https://help.basespace.illumina.com/Source/Informatics/BS/UploadFastqReq_swBS.htm?Highlight=fastq" target="_blank">strict Illumina FASTQ standard</a> in order to be consumed.</p> 
            </li> 
          </ul> 
        `
        break;
      default:
        break;
    }

    // For biosamples, convert flat format back to grouped format for modal
    let initiallySelectedForModal = this.selectedItems$.value ? this.selectedItems$.value : [];
    if (this.resourceType === SearchResourceType.Biosample && initiallySelectedForModal.length > 0) {
      initiallySelectedForModal = this.groupBiosamplesForModal(initiallySelectedForModal);
    }

    const chooserInput: IChooserModalInput = {
      hasMatcher: false,
      isResourceCreatable: this.formControl.allowResourceCreation,
      resourceType: this.resourceType,
      rowSelection: this.formControl.multiselect ? 'multiple' : 'single',
      title: this.resourceDictionary.chooserMessage,
      searchPlaceHolderText: 'Search',
      trackLibraryPreps: this.resourceType === SearchResourceType.Biosample,
      chooserContext: 'app-launch',
      titleInfoPopoverContent,
      tableId: this.tableId,
      initialColumnFilter: this.buildInitialProjectFilter(),
      searchDataSourceOptions: {
        useStrictPaging: true,

        // Combining searchQuery and extendedBaseQuery. This is sort of a hack when we need to add additions
        // to the baseQuery but only for the resource chooser in the context of the app launch form. A good example of this is 
        // for the biosample chooser where on the app launch page, we always want to search for biosamples that have
        // FastqDatasets but we don't want this everywhere on the basespace site. On file uploader we give user
        // ability to create a biosample so they can associate a new FASTQ dataset with the biosample so wouldn't want this param there.
        extendedBaseQuery: extendedBaseQuery,
        fileExtension: extensionFilter
      },
      initallySelectedItems: initiallySelectedForModal,
      requiredPermissions: this.formControl.allowedPermissions as any,
      supportedGenomeBuilds: this.formControl.genomeFilters ? this.formControl.genomeFilters.split(',') : null,
      currentUserId: this.bsAppLaunchFormService.currentUser.Id,
      newlyCreatedResourceId: newlyCreatedResourceId
    };
    const modalEvents = this.modalService.openModal(chooserInput, RadChooserModalComponent);

    this.subs.sink = modalEvents.confirm.subscribe((data: IChooserModalOutput<ISearchResultItem>) => {
      let selectedItems = data.selectedItems;
      
      // For biosamples, flatten the grouped modal output to composite format
      if (this.resourceType === SearchResourceType.Biosample) {
        selectedItems = this.flattenBiosampleResults(selectedItems);
      }
      
      const previousItems = this.selectedItems$.value || [];
      const orderedItems = this.orderSelectedItems(selectedItems, previousItems);
      this.selectedItems$.next(orderedItems);
      this.modalService.handleError(null);
    });

    const postNewResourceCreationAction = (data: IResource) => {
      const partialResource = this.generateSearchResultItem({ id: Number(data.Id), name: data.Name, type: null });
      if(!this.formControl.multiselect) {
        this.selectedItems$.next([partialResource]);
      } else {
        this.selectedItems$.next([...this.selectedItems$.value, partialResource]);

        this.openResourceChooser(data.Id);
      }

      this.modalService.handleError(null);
    };

    this.chooserModalService.createNewResourceFromChooser(
      modalEvents.new, 
      { resourceType: ResourceType.PROJECT }, 
      postNewResourceCreationAction,
      ModalComponentType.RAD
    );
  }

  /**
   * Builds the pre-applied, user-removable project filter to seed on the chooser table, or
   * undefined when none applies (no project-filter stamped, or no project column for this
   * resource type - e.g. runs). The filterValue is the backend-stamped project Id; the
   * displayName is resolved from the form's Output ProjectChooser when available, falling back
   * to the Id so the chooser still opens correctly filtered.
   */
  private buildInitialProjectFilter(): IInitialColumnFilter | undefined {
    const projectId = this.formControl.projectFilterId;
    const colId = this.formControl.projectFilterColId;
    if (!projectId || !colId) {
      return undefined;
    }

    const displayName = this.resolveProjectName(projectId) || projectId;
    return {
      colId,
      option: { filterValue: projectId, displayName }
    };
  }

  /**
   * Resolves a project name from the form's Output ProjectChooser control matching the given
   * project Id, or null when it can't be resolved cheaply (no API call). The chooser's own
   * project-option fetch reconciles the display name afterwards regardless.
   */
  private resolveProjectName(projectId: string): string | null {
    const projectChoosers = this.bsAppLaunchFormService.getControlsByType(
      this.bsAppLaunchFormService.formArray,
      ResourceChooserFormControl
    ).filter((control) =>
      control.resourceType === SearchResourceType.Project && control.valueType === 'Output'
    );

    for (const chooser of projectChoosers) {
      const match = (chooser.resources || []).find((resource) => String(resource.id) === String(projectId));
      if (match && match.name) {
        return match.name;
      }
    }

    return null;
  }

  removeSearchResult(searchResultItem: Partial<ISearchResultItem>) {
    const updatedSelectedItems = this.selectedItems$.value.filter((searchItem) => {
      return this.getResourceFromSearchResultItem(searchItem).Id !== this.getResourceFromSearchResultItem(searchResultItem).Id
    });
    this.selectedItems$.next(updatedSelectedItems);
  }

  generateSearchResultItem(initialResource: ResourceReference): Partial<ISearchResultItem> {
    // For biosamples, the ID and name are already composite (biosampleId::libraryPrepId, biosampleName: libraryPrepName)
    const resource: IResource = { Id: String(initialResource.id), Name: initialResource.name };

    switch(this.resourceType) {
      case SearchResourceType.Run:
        (resource as IRun).ExperimentName = initialResource.name;
        return { Run: resource };
      case SearchResourceType.Biosample:
        // The ID is composite (biosampleId::libraryPrepId) and name is composite (biosampleName: libraryPrepName)
        (resource as IBioSample).BioSampleName = initialResource.name;
        return { BioSample: resource as any };
      case SearchResourceType.Project:
        return { Project: resource };
      case SearchResourceType.File:
        return { DatasetFile: resource };
      case SearchResourceType.AppResultFile:
        return { AppResultFile: resource };
      case SearchResourceType.Sample:
        return { Sample: resource };
      case SearchResourceType.AppResult:
        return { AppResult: resource };
      case SearchResourceType.Dataset:
        return { Dataset: resource };
      default:
        throw new Error(`Have not implemented ${this.resourceType} yet`);  
    }
  }

  getResourceFromSearchResultItem(searchResultItem: ISearchResultItem): Partial<IResource> {
    return this.searchResourceDictionaryService.getSearchResultResource(searchResultItem, this.resourceType);
  }

  get selectionText(): string {
    return this.resourceDictionary.chooserMessage;
  }

  trackByResourceId = (index: number, item: ISearchResultItem): string => {
    return this.getResourceFromSearchResultItem(item).Id;
  }

  /**
   * Orders selected items to preserve the order of previously selected items.
   * Previously selected items that are still selected retain their original position.
   * Newly selected items are appended at the end in the order they were returned.
   */
  private orderSelectedItems(
    newSelectedItems: ISearchResultItem[],
    previousItems: ISearchResultItem[]
  ): ISearchResultItem[] {
    // Extract ordered keys from previous and new items
    const previousKeys = this.extractItemKeys(previousItems);
    const newKeys = this.extractItemKeys(newSelectedItems);
    const newKeysSet = new Set(newKeys);
    const previousKeysSet = new Set(previousKeys);

    // Build map from resource ID to item data (for reconstruction)
    const newItemsMap = new Map<string, ISearchResultItem>(
      newSelectedItems.map(item => [this.getResourceFromSearchResultItem(item).Id, item])
    );

    // Find retained keys (previous keys still in new selection)
    const retainedKeys = previousKeys.filter(key => newKeysSet.has(key));

    // Find newly added keys
    const newlyAddedKeys = newKeys.filter(key => !previousKeysSet.has(key));

    // Combine: retained + newly added
    const orderedKeys = [...retainedKeys, ...newlyAddedKeys];

    // Reconstruct items from ordered keys
    return this.reconstructItemsFromKeys(orderedKeys, newItemsMap);
  }

  /**
   * Extracts unique keys from items for ordering purposes.
   * With flat structure, all resource types (including biosamples) use simple resource IDs.
   */
  private extractItemKeys(items: ISearchResultItem[]): string[] {
    return items.map(item => this.getResourceFromSearchResultItem(item).Id);
  }

  /**
   * Reconstructs ISearchResultItem[] from ordered keys.
   * With flat structure, all resource types simply map keys to items.
   */
  private reconstructItemsFromKeys(
    orderedKeys: string[],
    itemsMap: Map<string, ISearchResultItem>
  ): ISearchResultItem[] {
    return orderedKeys.map(key => itemsMap.get(key));
  }

  /**
   * Converts modal output (grouped biosamples with selectedLibraryPrepIds) to flat composite format.
   * Each biosample+libraryprep combination becomes a separate ISearchResultItem.
   */
  private flattenBiosampleResults(groupedItems: ISearchResultItem[]): ISearchResultItem[] {
    const flatItems: ISearchResultItem[] = [];
    
    for (const item of groupedItems) {
      const biosample = item.BioSample;
      const libraryPrepIds = item.selectedLibraryPrepIds || [];
      
      for (const libraryPrepId of libraryPrepIds) {
        const libraryPrep = biosample.LibraryPreps.find((lp) => lp.Id === libraryPrepId);
        const compositeId = `${biosample.Id}::${libraryPrepId}`;
        const compositeName = `${biosample.BioSampleName}: ${libraryPrep.Name || libraryPrepId}`;
        
        // Create a new search result item for each biosample+libraryprep combination
        const flatItem: Partial<ISearchResultItem> = {
          BioSample: {
            Id: compositeId,
            BioSampleName: compositeName,
            LibraryPreps: biosample.LibraryPreps
          }
        };
        
        flatItems.push(flatItem as ISearchResultItem);
      }
    }
    
    return flatItems;
  }

  /**
   * Converts flat composite format back to grouped format for modal input.
   * Groups library preps back under their biosamples with selectedLibraryPrepIds.
   */
  private groupBiosamplesForModal(flatItems: ISearchResultItem[]): ISearchResultItem[] {
    const biosampleMap = new Map<string, { item: ISearchResultItem; libraryPrepIds: string[] }>();
    
    for (const item of flatItems) {
      const biosample = item.BioSample;
      const compositeId = String(biosample.Id);
      
      // Extract biosample ID and library prep ID from composite ID
      const [biosampleId, libraryPrepId] = compositeId.split('::');
      
      if (!biosampleMap.has(biosampleId)) {
        // Create a new grouped item with the original biosample ID
        const groupedBiosample = {
          Id: biosampleId,
          BioSampleName: biosample.BioSampleName.split(': ')[0], // Extract original biosample name
          LibraryPreps: biosample.LibraryPreps
        };
        
        biosampleMap.set(biosampleId, {
          item: { BioSample: groupedBiosample } as ISearchResultItem,
          libraryPrepIds: []
        });
      }
      
      biosampleMap.get(biosampleId).libraryPrepIds.push(libraryPrepId);
    }
    
    // Convert map to array with selectedLibraryPrepIds
    return Array.from(biosampleMap.values()).map(({ item, libraryPrepIds }) => ({
      ...item,
      selectedLibraryPrepIds: libraryPrepIds
    }));
  }

  openEditAllModal(): void {
    const resourceName = this.resourceDictionary.displayNamePlural.toLowerCase();
    
    const modalInput: IEditAllFieldsModalInput = {
      displayFields: this.formControl.displayFields,
      subtitle: `Edit metadata for all ${resourceName} selected.`
    };

    const modalEvents = this.modalService.openModal(modalInput, EditAllFieldsModalComponent);

    this.subs.sink = modalEvents.confirm.subscribe((output: IEditAllFieldsModalOutput) => {
      this.propagateValuesToAllRows(output.fieldValues);
      this.modalService.handleError(null);
    });
  }

  private propagateValuesToAllRows(fieldValues: any[]): void {
    if (!this.formControl.displayFieldsTabularFieldSetControl) {
      return;
    }

    // Iterate through each row in the tabular field set
    this.formControl.displayFieldsTabularFieldSetControl.controls.forEach((row: FormArray) => {
      // For each column, update if the value is non-null
      fieldValues.forEach((value, columnIndex) => {
        if (value !== null && value !== undefined) {
          const control = row.controls[columnIndex] as BasespaceFormControl<any>;
          if (control) {
            control.setValue(value);
          }
        }
      });
    });
  }

  destroy(): void {
    this.formControl.destroy();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

}
